import { useState } from 'react'
import {
  useEmployeeInvitation, type EmployeeInviteFields, type EmployeeRoleCategory, type EmployeeEmploymentType,
  type EmployeeCompensationModel, type EmployeePaymentFrequency,
} from '@/hooks/useEmployeeInvitation'
import { useSchoolVehicles } from '@/hooks/useSchoolVehicles'
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete'
import { supabase } from '@/lib/supabase'

const PHOTO_BUCKET = 'employee-photos'

const EMPTY_INVITE_FIELDS: EmployeeInviteFields = {
  email: '', fullLegalName: '', jobTitle: '', roleCategory: '', startDate: '', employmentType: '', phone: '',
  canViewStudents: false, canViewSchedules: false, canViewFinancials: false,
  ontarioDriverLicenceNumber: '', driverLicenceExpiryDate: '',
  instructorLicenceNumber: '', instructorLicenceExpiryDate: '', instructionTypes: [],
  address: '', assignedVehicleId: null,
  compensationModel: '', hourlyRate: '', salaryAmount: '', compensationEffectiveDate: '', paymentFrequency: '',
  photoPath: null,
}

const ROLE_CATEGORY_OPTIONS: { key: EmployeeRoleCategory; label: string }[] = [
  { key: 'instructor', label: 'Instructor' },
  { key: 'administrator', label: 'Administrator' },
  { key: 'receptionist', label: 'Receptionist' },
  { key: 'finance', label: 'Finance' },
]

const CREATE_ERROR_MESSAGES: Record<string, string> = {
  not_school_admin:        'You need to be an active owner or admin of this school to invite employees.',
  school_id_required:      'Select which school this invitation is for.',
  invalid_email:           'Enter a valid email address.',
  full_legal_name_required: 'Full legal name is required.',
  job_title_required:      'Job title is required.',
  role_category_required:  'Choose a role category.',
  start_date_required:     'Start date is required.',
  employment_type_required: 'Choose an employment type.',
  already_pending:         'There is already a pending invitation for this email at this school. Revoke it first to send a new one.',
  hourly_rate_required:    'Enter an hourly rate for the compensation block, or clear it to set compensation later.',
  salary_amount_required:  'Enter a salary amount for the compensation block, or clear it to set compensation later.',
  effective_date_required: 'Choose a compensation effective date, or clear the compensation block to set it later.',
  payment_frequency_required: 'Choose a payment frequency, or clear the compensation block to set it later.',
  internal_error:          'Something went wrong. Please try again.',
}

type StepKey = 'personal' | 'employment' | 'remuneration' | 'instructor'

const STEP_LABELS: Record<StepKey, string> = {
  personal:     'Personal details',
  employment:   'Employment details',
  remuneration: 'Remuneration',
  instructor:   'Instructor details',
}

function stepsFor(fields: EmployeeInviteFields): StepKey[] {
  const base: StepKey[] = ['personal', 'employment', 'remuneration']
  return fields.roleCategory === 'instructor' ? [...base, 'instructor'] : base
}

/** Pure: the single blocking error for a step, or null if it may proceed. Exported for tests. */
export function validateStep(step: StepKey, fields: EmployeeInviteFields): string | null {
  if (step === 'personal') {
    if (!fields.email.trim()) return 'Email is required.'
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fields.email.trim())) return 'Enter a valid email address.'
    if (!fields.fullLegalName.trim()) return 'Full legal name is required.'
    return null
  }
  if (step === 'employment') {
    if (!fields.roleCategory) return 'Choose a role category.'
    if (!fields.jobTitle.trim()) return 'Job title is required.'
    if (!fields.employmentType) return 'Choose an employment type.'
    if (!fields.startDate) return 'Start date is required.'
    return null
  }
  if (step === 'remuneration') {
    if (fields.compensationModel) {
      if (fields.compensationModel === 'hourly' && !fields.hourlyRate) return 'Enter an hourly rate, or clear the compensation model to set it later.'
      if (fields.compensationModel === 'salary' && !fields.salaryAmount) return 'Enter a salary amount, or clear the compensation model to set it later.'
      if (!fields.compensationEffectiveDate) return 'Choose a compensation effective date, or clear the compensation model to set it later.'
      if (!fields.paymentFrequency) return 'Choose a payment frequency, or clear the compensation model to set it later.'
    }
    return null
  }
  return null // instructor step is fully optional, same as it was inline
}

const fieldLabel: React.CSSProperties = {
  font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', display: 'block', marginBottom: 6,
}
const required = <span style={{ color: '#C0522A' }}>*</span>
const row: React.CSSProperties = { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }
const col: React.CSSProperties = { flex: '1 1 220px' }

function StepRail({ steps, currentIndex, onJump }: { steps: StepKey[]; currentIndex: number; onJump: (i: number) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 32 }}>
      {steps.map((key, i) => {
        const done = i < currentIndex
        const active = i === currentIndex
        const clickable = i <= currentIndex
        return (
          <div key={key} style={{ display: 'flex', alignItems: 'center', flex: i === steps.length - 1 ? '0 0 auto' : 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 96 }}>
              <button
                type='button'
                onClick={() => clickable && onJump(i)}
                disabled={!clickable}
                aria-current={active ? 'step' : undefined}
                style={{
                  width: 32, height: 32, borderRadius: '50%', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: done || active ? '#0F7C6C' : '#fff',
                  boxShadow: active
                    ? '0 0 0 4px rgba(15,124,108,0.16)'
                    : done ? 'none' : 'inset 0 0 0 1.5px rgba(27,42,74,0.2)',
                  cursor: clickable ? 'pointer' : 'default',
                  font: '600 13px Inter, sans-serif',
                  color: done || active ? '#fff' : 'rgba(27,42,74,0.4)',
                  flexShrink: 0,
                }}
              >
                {done ? (
                  <svg width='14' height='14' viewBox='0 0 24 24' fill='none'>
                    <path d='M5 13l4 4L19 7' stroke='#fff' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                ) : (i + 1)}
              </button>
              <div style={{
                marginTop: 8, font: active ? '600 12px Inter, sans-serif' : '500 12px Inter, sans-serif',
                color: active ? '#0F7C6C' : done ? '#1B2A4A' : 'rgba(27,42,74,0.4)',
                textAlign: 'center', lineHeight: 1.3,
              }}>
                {STEP_LABELS[key]}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#0F7C6C' : 'rgba(27,42,74,0.12)', marginTop: -20 }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

interface StepProps {
  fields: EmployeeInviteFields
  setField: <K extends keyof EmployeeInviteFields>(key: K, value: EmployeeInviteFields[K]) => void
  disabled: boolean
}

function PersonalStep({ fields, setField, disabled, schoolId }: StepProps & { schoolId: string | null }) {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const photoUrl = fields.photoPath ? supabase.storage.from(PHOTO_BUCKET).getPublicUrl(fields.photoPath).data.publicUrl : null

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !schoolId) return
    setUploadError('')
    setUploading(true)
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `${schoolId}/${crypto.randomUUID()}.${ext}`
      const { error: upErr } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, { contentType: file.type, upsert: false })
      if (upErr) { setUploadError('Could not upload the photo. Please try again.'); return }
      setField('photoPath', path)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
      <div style={{ flex: '0 0 180px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <div style={{
          width: 160, height: 160, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
          background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
        }}>
          {photoUrl ? (
            <img src={photoUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ font: '600 48px Inter, sans-serif', color: 'rgba(27,42,74,0.3)' }}>
              {fields.fullLegalName.trim().charAt(0).toUpperCase() || '?'}
            </span>
          )}
        </div>
        <label style={{
          display: 'inline-block', padding: '9px 18px', background: '#fff',
          border: '1px solid rgba(27,42,74,0.15)', borderRadius: 8,
          font: '600 12.5px Inter, sans-serif', color: '#1B2A4A',
          cursor: disabled || uploading ? 'not-allowed' : 'pointer', opacity: disabled || uploading ? 0.6 : 1,
        }}>
          {uploading ? 'Uploading…' : fields.photoPath ? 'Replace photo' : 'Upload photo'}
          <input type='file' accept='image/*' onChange={handlePhotoChange} disabled={disabled || uploading} style={{ display: 'none' }} />
        </label>
        <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 6 }}>Optional</div>
        {uploadError && <div style={{ font: '400 12px Inter, sans-serif', color: '#C0522A', marginTop: 4 }}>{uploadError}</div>}
      </div>

      <div style={{ flex: '1 1 320px', minWidth: 280 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={fieldLabel}>Full legal name {required}</label>
          <input value={fields.fullLegalName} onChange={e => setField('fullLegalName', e.target.value)} className='field-input' disabled={disabled} autoFocus />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={fieldLabel}>Email {required}</label>
          <input type='email' value={fields.email} onChange={e => setField('email', e.target.value)} placeholder='employee@example.com' className='field-input' disabled={disabled} />
        </div>
        <div>
          <label style={fieldLabel}>Phone</label>
          <input value={fields.phone} onChange={e => setField('phone', e.target.value)} className='field-input' disabled={disabled} />
        </div>
      </div>
    </div>
  )
}

function EmploymentStep({ fields, setField, disabled }: StepProps) {
  return (
    <>
      <div style={row}>
        <div style={col}>
          <label style={fieldLabel}>Role category {required}</label>
          <select value={fields.roleCategory} onChange={e => setField('roleCategory', e.target.value as EmployeeRoleCategory | '')} className='field-input' disabled={disabled}>
            <option value='' disabled>Select a category…</option>
            {ROLE_CATEGORY_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div style={col}>
          <label style={fieldLabel}>Job title {required}</label>
          <input value={fields.jobTitle} onChange={e => setField('jobTitle', e.target.value)} placeholder='e.g. Senior Instructor' className='field-input' disabled={disabled} />
        </div>
      </div>
      <div style={row}>
        <div style={col}>
          <label style={fieldLabel}>Employment type {required}</label>
          <select value={fields.employmentType} onChange={e => setField('employmentType', e.target.value as EmployeeEmploymentType | '')} className='field-input' disabled={disabled}>
            <option value='' disabled>Select…</option>
            <option value='full_time'>Full-time</option>
            <option value='part_time'>Part-time</option>
          </select>
        </div>
        <div style={{ ...col, flex: '0 1 200px' }}>
          <label style={fieldLabel}>Start date {required}</label>
          <input type='date' value={fields.startDate} onChange={e => setField('startDate', e.target.value)} className='field-input' disabled={disabled} />
        </div>
      </div>
      <div>
        <label style={fieldLabel}>School-specific permissions</label>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
            <input type='checkbox' checked={fields.canViewStudents} onChange={e => setField('canViewStudents', e.target.checked)} disabled={disabled} /> View students
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
            <input type='checkbox' checked={fields.canViewSchedules} onChange={e => setField('canViewSchedules', e.target.checked)} disabled={disabled} /> View schedules
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
            <input type='checkbox' checked={fields.canViewFinancials} onChange={e => setField('canViewFinancials', e.target.checked)} disabled={disabled} /> View financial records
          </label>
        </div>
      </div>
    </>
  )
}

function RemunerationStep({ fields, setField, disabled }: StepProps) {
  return (
    <>
      <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 18 }}>
        Optional — you can set or change this later from the employee's own record instead.
      </div>
      <div style={row}>
        <div style={{ ...col, flex: '0 1 220px' }}>
          <label style={fieldLabel}>Compensation model</label>
          <select value={fields.compensationModel} onChange={e => setField('compensationModel', e.target.value as EmployeeCompensationModel | '')} className='field-input' disabled={disabled}>
            <option value=''>Not set yet</option>
            <option value='hourly'>Hourly</option>
            <option value='salary'>Salary</option>
          </select>
        </div>
        {fields.compensationModel === 'hourly' && (
          <div style={{ ...col, flex: '0 1 180px' }}>
            <label style={fieldLabel}>Hourly rate</label>
            <input type='number' min='0' step='0.01' value={fields.hourlyRate} onChange={e => setField('hourlyRate', e.target.value)} className='field-input' disabled={disabled} />
          </div>
        )}
        {fields.compensationModel === 'salary' && (
          <div style={{ ...col, flex: '0 1 180px' }}>
            <label style={fieldLabel}>Salary amount</label>
            <input type='number' min='0' step='0.01' value={fields.salaryAmount} onChange={e => setField('salaryAmount', e.target.value)} className='field-input' disabled={disabled} />
          </div>
        )}
      </div>
      {fields.compensationModel && (
        <div style={row}>
          <div style={{ ...col, flex: '0 1 200px' }}>
            <label style={fieldLabel}>Effective date</label>
            <input type='date' value={fields.compensationEffectiveDate} onChange={e => setField('compensationEffectiveDate', e.target.value)} className='field-input' disabled={disabled} />
          </div>
          <div style={{ ...col, flex: '0 1 200px' }}>
            <label style={fieldLabel}>Payment frequency</label>
            <select value={fields.paymentFrequency} onChange={e => setField('paymentFrequency', e.target.value as EmployeePaymentFrequency | '')} className='field-input' disabled={disabled}>
              <option value=''>Select…</option>
              <option value='weekly'>Weekly</option>
              <option value='biweekly'>Biweekly</option>
              <option value='monthly'>Monthly</option>
            </select>
          </div>
        </div>
      )}
    </>
  )
}

function InstructorStep({ fields, setField, disabled, schoolId }: StepProps & { schoolId: string | null }) {
  const { vehicles, loading: vehiclesLoading } = useSchoolVehicles(schoolId)
  const availableVehicles = vehicles.filter(v => v.isActive)

  return (
    <>
      <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 18 }}>
        All optional, but recommended — the school is vouching for who shows up to drive a student.
      </div>
      <div style={{ font: '600 13px Inter, sans-serif', color: '#1B2A4A', marginBottom: 12 }}>Licensing</div>
      <div style={row}>
        <div style={col}>
          <label style={fieldLabel}>Instructor licence #</label>
          <input value={fields.instructorLicenceNumber} onChange={e => setField('instructorLicenceNumber', e.target.value)} className='field-input' disabled={disabled} />
        </div>
        <div style={{ ...col, flex: '0 1 200px' }}>
          <label style={fieldLabel}>Instructor licence expiry</label>
          <input type='date' value={fields.instructorLicenceExpiryDate} onChange={e => setField('instructorLicenceExpiryDate', e.target.value)} className='field-input' disabled={disabled} />
        </div>
      </div>
      <div style={row}>
        <div style={col}>
          <label style={fieldLabel}>Ontario driver's licence #</label>
          <input value={fields.ontarioDriverLicenceNumber} onChange={e => setField('ontarioDriverLicenceNumber', e.target.value)} className='field-input' disabled={disabled} />
        </div>
        <div style={{ ...col, flex: '0 1 200px' }}>
          <label style={fieldLabel}>Driver's licence expiry</label>
          <input type='date' value={fields.driverLicenceExpiryDate} onChange={e => setField('driverLicenceExpiryDate', e.target.value)} className='field-input' disabled={disabled} />
        </div>
      </div>

      <div style={{ font: '600 13px Inter, sans-serif', color: '#1B2A4A', margin: '20px 0 12px' }}>Vehicle</div>
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabel}>Assign a fleet vehicle</label>
        <select
          value={fields.assignedVehicleId ?? ''}
          onChange={e => setField('assignedVehicleId', e.target.value || null)}
          className='field-input'
          disabled={disabled || vehiclesLoading}
        >
          <option value=''>{vehiclesLoading ? 'Loading…' : availableVehicles.length === 0 ? 'No vehicles in the fleet yet' : 'No car assigned yet'}</option>
          {availableVehicles.map(v => (
            <option key={v.id} value={v.id}>
              {[v.year, v.make, v.model].filter(Boolean).join(' ')}{v.licencePlate ? ` — ${v.licencePlate}` : ''}
            </option>
          ))}
        </select>
        {availableVehicles.length === 0 && !vehiclesLoading && (
          <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 6 }}>
            Add a car to your fleet first (Fleet in the sidebar), or assign one to this employee later.
          </div>
        )}
      </div>

      <AddressAutocomplete label='Address' value={fields.address} onChange={addr => setField('address', addr)} disabled={disabled} />
    </>
  )
}

export function EmployeeOnboardingWizard({ schoolId, hasSchool, schoolsLoading, onSent, onCancel }: {
  schoolId: string | null
  hasSchool: boolean
  schoolsLoading: boolean
  onSent: (opts: { emailed: boolean }) => void
  onCancel: () => void
}) {
  const { creating, createInvitation } = useEmployeeInvitation()
  const [fields, setFields] = useState<EmployeeInviteFields>(EMPTY_INVITE_FIELDS)
  const [currentStep, setCurrentStep] = useState<StepKey>('personal')
  const [stepError, setStepError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [lastLink, setLastLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const steps = stepsFor(fields)
  const currentIndex = Math.max(0, steps.indexOf(currentStep))
  const effectiveStep = steps[currentIndex]
  const isLastStep = currentIndex === steps.length - 1

  const setField = <K extends keyof EmployeeInviteFields>(key: K, value: EmployeeInviteFields[K]) =>
    setFields(prev => ({ ...prev, [key]: value }))

  const handleCopyLink = () => {
    if (!lastLink) return
    navigator.clipboard.writeText(lastLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleSubmit() {
    if (!schoolId) { setSubmitError('Select which school this invitation is for.'); return }
    setSubmitError('')
    const result = await createInvitation(schoolId, fields)
    if (!result.ok) {
      setSubmitError(CREATE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not send the invitation. Please try again.')
      return
    }
    if (result.emailed) {
      setFields(EMPTY_INVITE_FIELDS)
      setCurrentStep('personal')
      onSent({ emailed: true })
    } else {
      if (result.token) setLastLink(`${import.meta.env.VITE_APP_URL}/accept-instructor-invite#token=${result.token}`)
      onSent({ emailed: false })
    }
  }

  function goNext() {
    const err = validateStep(effectiveStep, fields)
    if (err) { setStepError(err); return }
    setStepError('')
    if (isLastStep) { void handleSubmit(); return }
    setCurrentStep(steps[currentIndex + 1])
  }
  function goBack() {
    setStepError('')
    if (currentIndex === 0) return
    setCurrentStep(steps[currentIndex - 1])
  }
  function jumpTo(i: number) {
    if (i > currentIndex) return
    setStepError('')
    setCurrentStep(steps[i])
  }
  function handleDone() {
    setFields(EMPTY_INVITE_FIELDS)
    setCurrentStep('personal')
    setLastLink(null)
    onCancel()
  }

  if (!schoolsLoading && !hasSchool) {
    return (
      <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '22px 24px', marginBottom: 24 }}>
        <div className='banner-error'>
          You need to be an active owner or admin of a school to add employees.
        </div>
      </div>
    )
  }

  // Post-send fallback: the email couldn't go out, so hand the admin a link to share manually.
  if (lastLink) {
    return (
      <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '28px 24px', marginBottom: 24, textAlign: 'center' }}>
        <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>Invite created</div>
        <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 18 }}>
          The confirmation email couldn't be sent — share this link with them manually.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 560, margin: '0 auto 22px' }}>
          <div style={{
            flex: 1, font: '500 12.5px/1.4 "Courier New", monospace', color: '#1B2A4A',
            background: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.12)', borderRadius: 8,
            padding: '9px 12px', wordBreak: 'break-all', textAlign: 'left',
          }}>
            {lastLink}
          </div>
          <button
            onClick={handleCopyLink}
            style={{
              padding: '9px 14px', background: copied ? '#0F7C6C' : '#1B2A4A',
              border: 'none', borderRadius: 8, font: '600 12.5px Inter, sans-serif',
              color: '#fff', cursor: 'pointer', flexShrink: 0,
            }}
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
        <button onClick={handleDone} className='btn-teal' style={{ width: 'auto', padding: '10px 24px' }}>
          Done
        </button>
      </div>
    )
  }

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '26px 28px 24px', marginBottom: 24 }}>
      <StepRail steps={steps} currentIndex={currentIndex} onJump={jumpTo} />

      <div style={{ font: '600 17px Lora, serif', color: '#1B2A4A', marginBottom: 18 }}>
        {STEP_LABELS[effectiveStep]}
      </div>

      {effectiveStep === 'personal'     && <PersonalStep     fields={fields} setField={setField} disabled={creating} schoolId={schoolId} />}
      {effectiveStep === 'employment'   && <EmploymentStep   fields={fields} setField={setField} disabled={creating} />}
      {effectiveStep === 'remuneration' && <RemunerationStep fields={fields} setField={setField} disabled={creating} />}
      {effectiveStep === 'instructor'   && <InstructorStep   fields={fields} setField={setField} disabled={creating} schoolId={schoolId} />}

      {stepError && <div className='banner-error' style={{ marginTop: 6, marginBottom: 6 }}>{stepError}</div>}
      {submitError && <div className='banner-error' style={{ marginTop: 6, marginBottom: 6 }}>{submitError}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(27,42,74,0.06)' }}>
        <button
          type='button'
          onClick={onCancel}
          style={{ background: 'none', border: 'none', font: '600 13px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', cursor: 'pointer' }}
        >
          Cancel
        </button>
        <div style={{ display: 'flex', gap: 10 }}>
          {currentIndex > 0 && (
            <button
              type='button'
              onClick={goBack}
              disabled={creating}
              style={{
                padding: '10px 20px', background: '#fff', border: '1px solid rgba(27,42,74,0.15)',
                borderRadius: 8, font: '600 13px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer',
              }}
            >
              Back
            </button>
          )}
          <button type='button' onClick={goNext} disabled={creating} className='btn-teal' style={{ width: 'auto', padding: '10px 24px' }}>
            {creating ? 'Sending…' : isLastStep ? 'Send invite' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}
